import { useEffect, useRef, useState } from 'react'

// Define the minimum horizontal distance to consider as a swipe
export const SWIPE_THRESHOLD = 50

// Allowable vertical movement so that it qualifies as a horizontal swipe
export const VERTICAL_RESTRAINT = 100

type Props = {
  containerRef: React.RefObject<HTMLElement>
  direction: 'left' | 'right'
  handleSwipe: () => void
}

export function useTouchSwipe({ containerRef, handleSwipe, direction }: Props) {
  // Store the starting touch position
  const touchStartX = useRef(0)
  const touchStartY = useRef(0)

  const [ready, setReady] = useState(false)

  // allow initial render to set ref from dom
  useEffect(() => {
    setReady(true)
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0]
      touchStartX.current = touch.clientX
      touchStartY.current = touch.clientY
    }

    const handleTouchEnd = (e: TouchEvent) => {
      const touch = e.changedTouches[0]
      const deltaX = touch.clientX - touchStartX.current
      const deltaY = touch.clientY - touchStartY.current
      const isCorrectDirection = (deltaX > 0 && direction === 'left') || (deltaX < 0 && direction === 'right')

      if (isCorrectDirection && Math.abs(deltaX) > SWIPE_THRESHOLD && Math.abs(deltaY) < VERTICAL_RESTRAINT) {
        e.preventDefault()
        e.stopPropagation()
        handleSwipe()
      }
    }

    // Using { passive: false } ensures that preventDefault can be called.
    container.addEventListener('touchstart', handleTouchStart, { passive: false })
    container.addEventListener('touchend', handleTouchEnd, { passive: false })

    return () => {
      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchend', handleTouchEnd)
    }
  }, [containerRef, handleSwipe, direction, ready])
}
